import { useEffect, useRef, useState } from "react";
import { formatEuro, type ContractLineDto } from "@klantpunt/shared";

/** Kort bedrag voor de as: € 4,8k. */
const short = (cents: number) => {
  const e = cents / 100;
  return e >= 1000 ? `€ ${(e / 1000).toLocaleString("nl-NL", { maximumFractionDigits: 1 })}k` : `€ ${Math.round(e)}`;
};
const pct = (v: number) => `${v >= 0 ? "+" : ""}${v.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}%`;

function ticks(max: number): number[] {
  if (max <= 0) return [0];
  const raw = max / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  return Array.from({ length: Math.ceil(max / step) + 1 }, (_, k) => k * step);
}

export interface PriceYear {
  year: number;
  cents: number;
  /** Eerste prijs doorgerekend met de index: wat de post zou kosten als hij alleen met de index meestijgt. */
  indexCents: number;
  /** Wijziging tegenover het jaar ervoor. */
  change: number | null;
}

/** Prijs per jaar (op 1 juli) van de eerste prijs tot dit jaar, of tot de laatst bekende prijs als die later ingaat. */
export function priceYears(l: ContractLineDto, indexPercent: number, thisYear: number): PriceYear[] {
  const value = (p: ContractLineDto["prices"][number]) =>
    l.billing === "cost_plus" ? p.hourlyRateCents : p.amountCents;
  const ps = l.prices.filter((p) => value(p) != null).sort((a, b) => a.validFrom.localeCompare(b.validFrom));
  if (!ps.length) return [];
  const first = Number(ps[0]!.validFrom.slice(0, 4));
  const last = Math.max(thisYear, Number(ps.at(-1)!.validFrom.slice(0, 4)));
  const out: PriceYear[] = [];
  for (let y = first; y <= last; y++) {
    const day = `${y}-07-01`;
    let p = ps[0]!;
    for (const x of ps) if (x.validFrom <= day) p = x;
    const cents = value(p)!;
    const prev = out.at(-1);
    out.push({
      year: y,
      cents,
      indexCents: Math.round(value(ps[0]!)! * Math.pow(1 + indexPercent / 100, y - first)),
      change: prev ? ((cents - prev.cents) / prev.cents) * 100 : null,
    });
  }
  return out;
}

/**
 * Prijsverloop van een post (docs/CONTRACTEN.md, C3): staaf per jaar met de prijs die dan gold, en ernaast in
 * neutraal grijs de eerste prijs doorgerekend met de index. Hover of focus toont de wijziging; de prijstabel
 * eronder is de tekstversie.
 */
export function PriceChart({ line, indexPercent }: { line: ContractLineDto; indexPercent: number }) {
  const data = priceYears(line, indexPercent, new Date().getFullYear());
  const [hover, setHover] = useState<number | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(360);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(240, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (data.length < 2) return null;
  const H = 150;
  const pad = { l: 52, r: 4, t: 8, b: 22 };
  const max = Math.max(...data.map((d) => Math.max(d.cents, d.indexCents)));
  const tk = ticks(max);
  const top = tk.at(-1) || 1;
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const slot = iw / data.length;
  const bw = Math.min(14, slot * 0.32);
  const yOf = (v: number) => pad.t + ih - (v / top) * ih;
  const h = hover != null ? data[hover] : null;
  const what = line.billing === "cost_plus" ? "Uurtarief" : "Prijs";
  return (
    <figure className="bchart pricechart">
      <figcaption className="small muted">{what} per jaar</figcaption>
      <ul className="bchart-legend" aria-label="Legenda">
        <li>
          <i className="wt mc-now" aria-hidden="true" />
          {what} in het contract
        </li>
        <li>
          <i className="wt mc-ly" aria-hidden="true" />
          Met index {indexPercent.toLocaleString("nl-NL")}%
        </li>
      </ul>
      <div className="bchart-wrap" ref={wrap}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${what} per jaar van ${line.description}`}>
          {tk.map((v) => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={yOf(v)} y2={yOf(v)} className="bchart-grid" />
              <text x={pad.l - 6} y={yOf(v)} className="bchart-axis" textAnchor="end" dominantBaseline="middle">
                {short(v)}
              </text>
            </g>
          ))}
          {data.map((d, k) => {
            const cx = pad.l + k * slot + slot / 2;
            const label = `${d.year}: ${formatEuro(d.cents)}${d.change != null ? ` (${pct(d.change)})` : ""}, met index ${formatEuro(d.indexCents)}`;
            return (
              <g
                key={d.year}
                tabIndex={0}
                role="img"
                aria-label={label}
                className={`bchart-bar${hover === k ? " on" : ""}`}
                onMouseEnter={() => setHover(k)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(k)}
                onBlur={() => setHover(null)}
              >
                <rect x={pad.l + k * slot} y={pad.t} width={slot} height={ih} className="bchart-hit" />
                <rect
                  x={cx - 1 - bw}
                  y={yOf(d.cents)}
                  width={bw}
                  height={Math.max(0, yOf(0) - yOf(d.cents))}
                  rx={2}
                  className="mc-now bchart-seg"
                />
                <rect
                  x={cx + 1}
                  y={yOf(d.indexCents)}
                  width={bw}
                  height={Math.max(0, yOf(0) - yOf(d.indexCents))}
                  rx={2}
                  className="mc-ly bchart-seg"
                />
                <text x={cx} y={H - 6} className="bchart-axis" textAnchor="middle">
                  {d.year}
                </text>
              </g>
            );
          })}
          <line x1={pad.l} x2={W - pad.r} y1={yOf(0)} y2={yOf(0)} className="bchart-base" />
        </svg>
        {h && (
          <div className="bchart-tip" role="status" style={{ left: `${((pad.l + (hover! + 0.5) * slot) / W) * 100}%` }}>
            <strong>{h.year}</strong>
            <span className="block small">
              {formatEuro(h.cents)}
              {h.change != null && ` · ${pct(h.change)} op het jaar ervoor`}
            </span>
            <span className="block small">
              Met index {formatEuro(h.indexCents)} ({pct((h.cents / h.indexCents - 1) * 100)})
            </span>
          </div>
        )}
      </div>
    </figure>
  );
}
